import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { toAuthErrorMessage } from "../../../lib/authErrorMessage";

export function InterviewerLogin() {
  // ローディングは AuthContext が持つ（管理者ログイン画面と同じ理由）
  const { signInWithGoogle, isAuthenticated, isLoading, authErrorReason } =
    useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(searchParams.get("error"));

  // signInWithGoogle() の戻り直後は AuthContext の isAuthenticated 反映前
  // (Firebase の onAuthStateChanged → fetchMyProfile 完了まで非同期) なので、
  // ここで手動 navigate するとログイン直後に /login へ弾き返される。
  // isAuthenticated を監視して Navigate に委譲する。
  if (isAuthenticated) {
    return <Navigate to="/employee_interview/interviewer/dashboard" replace />;
  }

  const handleGoogleLogin = async () => {
    setError(null);
    try {
      await signInWithGoogle();
      // 成功時は isAuthenticated 反映後に上の Navigate が走る
    } catch (err) {
      setError(err instanceof Error ? err.message : "ログインに失敗しました");
    }
  };

  const errorMessage = error
    ? error === "unauthorized"
      ? "インタビュアー権限がありません"
      : error === "cancelled"
        ? "認証がキャンセルされました"
        : error === "no_code" || error === "failed"
          ? "ログインに失敗しました"
          : error
    : authErrorReason
      ? toAuthErrorMessage(authErrorReason)
      : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">インタビュアーログイン</CardTitle>
          <CardDescription>
            Googleアカウントでログインしてください
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage && (
            <div className="rounded-lg bg-[var(--color-red-50)] p-3 text-center text-[var(--color-red-500)] text-sm">
              {errorMessage}
            </div>
          )}

          <Button
            onClick={handleGoogleLogin}
            className="w-full gap-2"
            disabled={isLoading}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? "ログイン中..." : "Googleでログイン"}
          </Button>

          <p className="text-center text-[var(--color-text-tertiary)] text-xs">
            インタビュアー権限を持つGoogleアカウントでログインしてください
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
